<template>
    <div>
        <form class="col-md-8 mx-auto" @submit.prevent="onSubmit">
            <div class="loader" v-if="loading"></div>
            <div v-else>
                <page-title :title="$t('externalSupplier.title')" :showTitle="false"></page-title>

                <div class="btn-group" role="group" aria-label="Basic example">
                    <button type="button" class="btn btn-primary" @click="backToRegister">
                        <i class="fas fa-th-list"></i>
                        {{ $t('backToList') }}
                    </button>
                </div>

                <div class="card">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('externalSupplier.contractData') }}</div>
                    </div>
                    <div class="card-body">
                        <div v-for="(contract,index) in externalSupplier.contracts"
                        :key="index"
                             :accesskey="contract.id"
                             class="k-display-flex align-items-sm-center">
                            <div>
                                <contract :contract="contract" :showButton="false"></contract>
                                <div v-if="contract.tariffs && Array.isArray(contract.tariffs)">
                                    <div v-for="tariffId in contract.tariffs" :key="tariffId">
                                        <TariffEntityDetails :id="tariffId" :showContractInfo="false"
                                                             :allowEdit="true"></TariffEntityDetails>
                                    </div>
                                </div>
                                <button type="button" class="btn btn-danger form-group mt-2"
                                        @click="deleteContract(index,contract)">
                                    <i class="fas fa-trash-alt btn-danger pointer position-relative"></i>
                                    {{$t('buttons.delete.btnText')}}
                                </button>
                            </div>
                        </div>
                        <hr/>
                        <div class="form-group">
                            <button type="button"
                                    class="btn btn-primary btn-md"
                                    @click="addContract">
                                <i class="fas fa-plus mr-2"></i>
                                {{ $t('externalSupplier.addContract') }}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="card mt-5">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('externalSupplier.company') }}</div>
                    </div>
                    <div class="card-body row">
                        <div class="form-group col-4 ">
                            <label :for="'number' + _uid" class="font-weight-bold">{{ $t('externalSupplier.id') }}</label>
                            <input type="number"
                                   class="form-control"
                                   :id="'id' + _uid"
                                   :name="'id' + _uid"
                                   v-model="externalSupplier.id"
                                   :data-vv-as="$t('externalSupplier.id')"
                                   readonly/>
                        </div>
                        <div class="form-group col-4">
                            <label :for="'number' + _uid" class="font-weight-bold">{{ $t('externalSupplier.status')
                                }}</label>
                            <toggle :value="externalSupplier.deactivated" v-model="externalSupplier.deactivated"></toggle>
                        </div>
                        <div class="form-group col-4">
                            <label :for="'number' + _uid" class="font-weight-bold">{{ $t('externalSupplier.isCompany')
                                }}</label>
                            <toggle :value="externalSupplier.isCompany"
                                    v-model="externalSupplier.isCompany"
                                    @input="changeCompanyType"></toggle>
                        </div>
                    </div>
                    <div class="card-body" v-if="externalSupplier.company">
                        <company :company="externalSupplier.company"
                            :showCompanyContact="false"
                            :showCompanyManagerButton="false"></company>
                    </div>
                    <div class="card-body" v-if="externalSupplier.person">
                        <person :person="externalSupplier.person"
                                :requiredIdentification="false"
                                :showExtended="false"></person>
                    </div>
                </div>
                <div class="card mt-5">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('externalSupplier.contactPersons') }}</div>
                    </div>
                    <div class="card-body">
                        <div v-for="(contactPerson,index) in externalSupplier.contacts" :key="index">
                            <hr/>
                            <div class="k-display-flex align-items-sm-center">
                                <contactPerson :person="contactPerson"></contactPerson>
                                <i class="fas fa-trash-alt btn-danger pointer position-relative"
                                   @click="deleteContactPerson(index,contactPerson)"></i>
                            </div>
                        </div>
                        <div class="mt-2 ml-2 mb-1">
                            <button type="button"
                                    class="btn btn-primary btn-md mr-2"
                                    @click="addContactPerson">
                                <i class="fas fa-plus mr-2"></i>
                                {{ $t('externalSupplier.addContactPerson') }}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="card mt-5">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('otherFields.otherFields') }}</div>
                    </div>
                    <div class="card-body">
                        <otherFields :otherFields="externalSupplier.otherFields"></otherFields>
                    </div>
                </div>

                <div class="card mt-3">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('registers.lectors.attachedDocuments') }}</div>
                    </div>
                    <div class="card-body">
                        <div v-for="(document,index) in externalSupplier.documents"
                            :key="index"
                             class="mt-2 border border-light-2 p-2 k-display-flex">
                            <file-download :file="document"
                                           class="col-12"
                                           :type="document.docTypeCode">
                            </file-download>
                            <i class="fas fa-trash-alt k-align-self-center btn-danger pointer position-relative"
                               @click="removeDocument(document,index)"></i>
                        </div>
                        <div v-for="(file,index) in externalSupplier.files"
                            :key="index"
                             class="mt-2 border border-light-2 p-2 k-display-flex">
                            <file-upload :file="file" class="col-12"></file-upload>
                            <i class="fas fa-trash-alt k-align-self-center btn-danger pointer position-relative"
                               @click="removeFile(index)"></i>
                        </div>
                        <button type="button" class="btn btn-secondary mt-3" @click="addFile">{{ $t('publicOrder.addFile')
                            }}
                        </button>
                    </div>
                </div>

                <AuxiliaryRegister ref="auxiliaryRegisterRef"
                                   :register-id="AuxiliaryRegisters.External_suppliers_Contracts"
                                   register-name="ExternalSupplier"
                                   :entry-id="externalSupplier.auxRegisterEntityId"></AuxiliaryRegister>

                <div class="text-center mt-2 mb-5">
                    <button type="button" class="btn btn-primary btn" @click="onSubmit">
                        <i class="fas fa-save"></i>
                        {{ $t('buttons.save.btnText') }}
                    </button>
                </div>
            </div>
        </form>
        <div v-if="!isNew">
            <HistoryRecordsGrid :id="id"
                                :url="'api/history/externalSupplier'"
                                :columns="externalSupplierHistoryGridColumns"
                                :schemaModelFields="externalSupplierHistoryGridSchemaModelFields"
                                :componentName="'ExternalSupplierHistory'">
            </HistoryRecordsGrid>
        </div>
    </div>

</template>

<script>
    import PersonComponent from '@/components/person/Person';
    import OtherFields from "@/components/otherFields/OtherFields.vue";
    import PageTitle from "@/components/common/PageTitle";
    import CompanyComponent from "@/components/company/Company";
    import ContractComponent from "@/components/contract/Contract"
    import ContactPerson from "@/components/person/ContactPerson";
    import Toggle from "@/components/toggle/Toggle";
    import { ExternalSupplier, Person, Contract, File, Company } from '@/models/models';
    import FileUpload from '@/components/common/FileUpload';
    import FileDownload from '@/components/common/FileDownload';
    import TariffEntityDetails from '@/components/finance/TariffEntityDetails';
    import HistoryRecordsGrid from '@/components/history/HistoryRecordsGrid';
    import { AuxiliaryRegisters } from '@/models/enums';
    import { externalSupplierHistoryGridColumns, externalSupplierHistoryGridSchemaModelFields } from '@/settings/historyGridsSettings';
    import AuxiliaryRegister from '@/components/dynamic/AuxiliaryRegister';

    export default {
        name: 'ExternalSupplierEditor',
        components: {
            PageTitle,
            OtherFields,
            'company': CompanyComponent,
            ContactPerson,
            Toggle,
            'contract': ContractComponent,
            'file-upload': FileUpload,
            'file-download': FileDownload,
            'person': PersonComponent,
            TariffEntityDetails,
            HistoryRecordsGrid,
            AuxiliaryRegister
        },
        data() {
            return {
                id: null,
                loading: false,
                externalSupplier: new ExternalSupplier(),
                isSaved: false,
                externalSupplierHistoryGridColumns: externalSupplierHistoryGridColumns,
                externalSupplierHistoryGridSchemaModelFields: externalSupplierHistoryGridSchemaModelFields,
                AuxiliaryRegisters: AuxiliaryRegisters
            };
        },
        methods: {
            onSubmit() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        this.externalSupplier.auxRegisterModel = this.$refs.auxiliaryRegisterRef.getDynamicRegisterModelForSubmit();
                        if (this.externalSupplier.company) {
                            this.externalSupplier.company.contact = null;
                        }
                        if (this.id) {
                            this.edit();
                        } else {
                            this.create();
                        }
                    }
                });
            },
            addFile() {
                this.externalSupplier.files.push(new File({ fileName: '', description: '' }));
            },
            removeFile(index) {
                this.externalSupplier.files.splice(index, 1);
            },
            removeDocument(document, index) {
                this.externalSupplier.documents.splice(index, 1);
                this.externalSupplier.deletedDocumentsIds.push(document.id);
            },
            addContactPerson(event) {
                this.externalSupplier.contacts.push(new Person());
            },
            addContract(event) {
                this.externalSupplier.contracts.push(new Contract());
            },
            deleteContactPerson: function (m, person) {
                if (person.id) {
                    this.externalSupplier.deletedContacts.push(person.id);
                }
                this.externalSupplier.contacts.splice(m, 1);
            },
            deleteContract: function (m, contract) {
                this.externalSupplier.contracts.splice(m, 1);
                if (contract.id) {
                    this.externalSupplier.deletedContracts.push(contract.id);
                }
            },
            backToRegister() {
                this.$router.push("/registers/externalSuppliers");
            },
            create() {
                this.loading = true;
                this.$http
                    .post("/api/externalSupplier", this.externalSupplier)
                    .then(result => {
                        this.loading = false;
                        this.isSaved = true;
                        this.$vs.notify({
                            title: this.$t("notifications.success"),
                            text: this.$t("notifications.successfulSavedData"),
                            position: "top-center",
                            color: "success"
                        });
                        this.uploadFiles(result.data);
                        this.backToRegister();
                    })
                    .catch(error => {
                        this.loading = false;
                        this.$vs.notify({
                            title: this.$t("notifications.error"),
                            text: this.$helpers.errorText(error),
                            position: "top-center",
                            color: "danger"
                        });
                    });
            },
            edit() {
                this.loading = true;
                this.$http
                    .put("/api/externalSupplier", this.externalSupplier)
                    .then(result => {
                        this.loading = false;
                        this.isSaved = true;
                        this.$vs.notify({
                            title: this.$t("notifications.success"), // TODO:
                            text: this.$t("notifications.successfulSavedData"), // TODO:
                            position: "top-center",
                            color: "success"
                        });
                        this.uploadFiles(this.id);
                        this.backToRegister();
                    })
                    .catch(error => {
                        this.loading = false;
                        this.$vs.notify({
                            title: this.$t("notifications.error"), // TODO:
                            text: this.$helpers.errorText(error), // TODO:
                            position: "top-center",
                            color: "danger"
                        });
                    });
            },
            changeCompanyType() {
                if (this.externalSupplier.isCompany) {
                    this.externalSupplier.company = new Company();
                    this.externalSupplier.company.contact = null;
                    this.externalSupplier.person = null;
                } else {
                    this.externalSupplier.person = new Person();
                    this.externalSupplier.company = null;
                }
            },
            uploadFiles(registerId) {
                // TODO: Loading with upload files text for example
                this.loading = true;
                if (this.externalSupplier.files.length > 0) {
                    let formData = new FormData();
                    let descriptions = [];
                    this.externalSupplier.files.forEach(file => {
                        formData.append('files', file.content, file.fileName);
                        descriptions.push({
                            fileName: file.fileName,
                            description: file.fileDescription,
                            length: file.content.size
                        });
                    });

                    try {
                        const descJson = JSON.stringify(descriptions);
                        formData.append('descriptionJson', descJson);
                    } catch (e) {
                        console.error(e);
                    }

                    this.$http.post(`/api/externalSupplier/uploadfiles/${registerId}`, formData).then(result => {
                        this.backToRegister();
                        this.loading = false;
                        this.$vs.notify({
                            title: this.$t('notifications.success'),
                            text: this.$t('notifications.successUploadingFiles'),
                            position: 'top-center',
                            color: 'success'
                        });
                    }).catch(error => {
                        this.loading = false;
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
                } else {
                    this.backToRegister();
                }
            },
            loadData() {
                this.loading = true;
                this.$http
                    .get(`/api/externalSupplier/${this.id}`)
                    .then(result => {
                        this.externalSupplier = new ExternalSupplier(result.data);
                        this.$store.initialModel = _.cloneDeep(this.externalSupplier);
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('buttons.load.btnText'),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    }).then(() => { this.loading = false; });
            },
            difference(object, base) {
                return _.transform(object, (result, value, key) => {
                    if (!_.isEqual(value, base[key])) {
                        result[key] = _.isObject(value) && _.isObject(base[key]) ? this.difference(value, base[key]) : value;
                    }
                });
            }
        },
        mounted() {
            this.id = this.$route.params.id;
            if (this.id) {
                this.loadData();
            } else {
                this.externalSupplier.contacts.push(new Person());
                this.externalSupplier.contracts.push(new Contract());
                this.externalSupplier.person = new Person();
            }
            this.$store.initialModel = _.cloneDeep(this.externalSupplier);
        },
        beforeRouteLeave(to, from, next) {
            if (!_.isEqual(this.$store.initialModel, this.externalSupplier) && !this.isSaved) {
                const answer = window.confirm(this.$t('notifications.leavePage'))
                if (answer) {
                    next()
                } else {
                    next(false)
                }
            } else {
                next()
            }
        },
        computed: {
            isNew() {
                const currentId = Number(this.id);

                return isNaN(currentId) || currentId <= 0;
            }
        }
    };
</script>

<style scoped>
</style>
